// ============================================================
//  src/pages/auth/RegisterPage.js
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { registerUser as apiRegister } from '../../api/client';
import { getError } from '../../utils/helpers';
import AuthShell, { AuthAlert, PasswordStrength } from './AuthShell';

export default function RegisterPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    fname: '', lname: '', email: '', phone: '', password: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = !form.fname.trim() || !form.lname.trim() || !form.email.trim() || !form.phone.trim() || !form.password;
    if (missing) { setError('Please fill in all the fields.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await apiRegister({
        fname:    form.fname.trim(),
        lname:    form.lname.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim(),
        password: form.password,
      });
      if (!data?.success) {
        setError(data?.message || 'Could not create your account.');
        return;
      }
      toast.success('Check your email for a verification code.');
      nav('/verify-email', { state: { email: data?.data?.email || form.email.trim() } });
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      headline={<>Turn WhatsApp chats into <em>finished sales.</em></>}
      sub="Build a bot that answers customers, takes orders and confirms payments on its own — no code required."
      points={['Free plan — no card required', 'Connect your number in minutes', 'Hand off to your team any time']}
      switchText="Already have an account?"
      switchTo="/login"
      switchLabel="Sign in"
      title="Create your account"
      subtitle="Start free. It takes less than a minute."
    >
      <AuthAlert>{error}</AuthAlert>

      <form onSubmit={submit} noValidate>
        <div className="auth-grid-2">
          <div className="auth-field">
            <label htmlFor="reg-fname">First name</label>
            <div className="auth-field-wrap">
              <input id="reg-fname" type="text" value={form.fname} onChange={set('fname')} placeholder="Ada" autoComplete="given-name" autoFocus required />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="reg-lname">Last name</label>
            <div className="auth-field-wrap">
              <input id="reg-lname" type="text" value={form.lname} onChange={set('lname')} placeholder="Okafor" autoComplete="family-name" required />
            </div>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="reg-email">Email</label>
          <div className="auth-field-wrap">
            <i className="fas fa-envelope fi" />
            <input id="reg-email" type="email" inputMode="email" value={form.email} onChange={set('email')} placeholder="you@business.com" autoComplete="email" autoCapitalize="none" spellCheck={false} required />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="reg-phone">Phone number</label>
          <div className="auth-field-wrap">
            <i className="fas fa-phone fi" />
            <input id="reg-phone" type="tel" inputMode="tel" value={form.phone} onChange={set('phone')} placeholder="+234 801 234 5678" autoComplete="tel" required />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="reg-pw">Password</label>
          <div className="auth-field-wrap">
            <i className="fas fa-lock fi" />
            <input
              id="reg-pw"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={set('password')}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? 'Hide password' : 'Show password'}
            >
              <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          </div>
          <PasswordStrength value={form.password} />
        </div>

        <button type="submit" className="btn-auth" disabled={loading}>
          {loading
            ? <><span className="spinner" /> Creating account…</>
            : <>Create free account <i className="fas fa-arrow-right" /></>}
        </button>

        <p className="auth-terms">
          By creating an account you agree to our Terms of Service and Privacy Policy.
        </p>
      </form>

      <div className="auth-bottom">
        Already have an account? <Link to="/login">Sign in</Link>
      </div>
    </AuthShell>
  );
}
